<template>
  <v-container
    id="user-details"
    tag="section"
  >
    <v-row>
      <v-col class="pb-0">
        <div class="user-details-header">
          <div class="user-details-header__left">
            <user-name
              :user-id="parseInt($route.params.id)"
              :resolved="resolved"
              :editable="isInternal"
              :saved-name="savedUserData"
            />
            <div @click="checkAdmin">
              <user-role
                :org="currentSelectedOrg"
                :editable="currentSelectedOrg.id !== undefined && canAdminSelectedOrg"
                :disabled="disableEditing"
                :saved-role="userRoleOnOrg"
              />
            </div>
          </div>
          <div class="user-details-header__right">
            <user-details-right-header
              :org="currentSelectedOrg"
              :global-alerts-editable="isInternal"
              :selected-user="savedUserData"
            />
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card style="margin-left:14px; margin-right:14px; margin-top:10px;">
          <v-tabs
            v-model="activeTab"
            background-color="transparent"
            color="muted"
          >
            <v-tabs-slider color="muted" />
            <valkyrie-tab
              text="Settings"
              icon="$faCog"
            />
            <valkyrie-tab
              text="Managed Properties"
              icon="$faHome"
            />
            <valkyrie-tab
              v-if="hasActiveAutoResolveIntegration"
              text="AutoResolve"
              icon="$faCommentCheck"
            />
            <v-tabs-items
              v-model="activeTab"
              class="transparent"
            >
              <v-tab-item>
                <div style="min-height: 400px">
                  <user-notification-settings
                    :saved-user-data="savedUserData"
                    :notification-settings="notificationSettings"
                  />
                </div>
              </v-tab-item>
              <v-tab-item
                class="tab-window"
              >
                <div style="min-height: 400px">
                  <user-property-settings
                    :user-id="userId"
                    editable
                  />
                </div>
              </v-tab-item>
              <v-tab-item>
                <div style="min-height: 400px">
                  <auto-resolve-setting
                    v-if="hasActiveAutoResolveIntegration"
                    :saved-user-data="savedUserData"
                    :auto-resolve="notificationSettings.addAutoResolveMessageMirroringNotifications"
                  />
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-tabs>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import UserName from '@/components/user/UserName'
  import UserPropertySettings from '@/components/user/PropertySettings'
  import AutoResolveSetting from '@/components/user/AutoResolveSetting'
  import UserNotificationSettings from '@/components/user/UserNotificationSettings'
  import UserRole from '@/components/user/UserRole'
  import UserDetailsRightHeader from '@/components/user/UserDetailsRightHeader'
  import ValkyrieTab from '@/components/base/ValkyrieTab'
  import { OCCUPANCY } from '../../helpers/feature-constants'
  import axios from 'axios'

  export default {
    name: 'AdminUser',
    components: {
      UserName,
      UserRole,
      UserDetailsRightHeader,
      AutoResolveSetting,
      UserPropertySettings,
      UserNotificationSettings,
      ValkyrieTab,
    },
    data: () => ({
      resolved: false,
      activeTab: 0,
      hasActiveAutoResolveIntegration: false,
    }),
    computed: {
      ...mapGetters({
        savedUserData: 'Users/userData',
        notificationSettings: 'Users/userNotificationSettings',
        isInternal: 'Permissions/isInternal',
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
        canAdminSelectedOrg: 'Permissions/canAdminSelectedOrg',
        canAdminOrg: 'Permissions/canAdminOrg',
        userRoleOnOrg: 'Permissions/userRoleOnOrg',
        isOrgOwner: 'Permissions/isOrgOwner',
      }),
      userId () {
        return this.$route.params.id
      },
      disableEditing () {
        return !this.isOrgOwner && (this.userRoleOnOrg === 'Administrator' || this.userRoleOnOrg === 'Account Owner') && !this.isInternal
      },
      orgId () {
        return this.currentSelectedOrg.id
      },
      orgIsSelected () {
        return this.currentSelectedOrg.id
      },
    },
    watch: {
      async currentSelectedOrg (newOrg) {
        if (newOrg.id && this.canAdminOrg(newOrg.id)) {
          this.resolved = false
          this.$store.dispatch('Organizations/fetchOrgFeatureEligibility', { orgId: newOrg.id, feature: OCCUPANCY })
          await this.fetchOrgAutoResolveIntegration()
          this.resolved = true
        } else this.$router.push('/')
      },
    },
    created () {
      this.fetchUserData()
      if (this.orgIsSelected) this.$store.dispatch('Organizations/fetchOrgFeatureEligibility', { orgId: this.currentSelectedOrg.id, feature: OCCUPANCY })
    },
    mounted () {
      this.checkAdmin()
    },
    beforeDestroy () {
      this.$store.commit('Users/setUserData', {})
    },
    methods: {
      checkAdmin () {
        const accountType = (this.userRoleOnOrg === 'Account Owner') ? 'an owner account' : 'another administrator'
        if (this.disableEditing) this.$store.dispatch('updateMessages', { type: 'info', text: `You cannot edit ${accountType}` }, { root: true })
      },
      async fetchUserData () {
        try {
          await this.$store.dispatch('Users/fetchUserData', this.userId)
          await this.$store.dispatch('Users/fetchUserNotificationSettings', this.userId)
          if (this.orgIsSelected) await this.fetchOrgAutoResolveIntegration()
        } finally {
          this.resolved = true
        }
      },
      async fetchOrgAutoResolveIntegration () {
        try {
          const res = await axios.get(`${this.$store.state.baseURL}/user/notification/autoresolve/${this.orgId}`)
          this.hasActiveAutoResolveIntegration = res.data.data.isActive
        } catch (err) {
          this.hasActiveAutoResolveIntegration = false
        }
      },
    },
  }
</script>
<style lang="scss">
#user-details {
  .user-details-header {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    margin: auto 14px;
  }
  padding-top:0;
  .user-area-card{
    width:16rem;
    min-width:245px;
    min-height:245px;
    .user-box {
      height:245px;
      .edit-btn-container {
        display:flex;
        justify-content:center;
        max-width:35px;
        .edit-btn {
          min-width:0;
          width:1rem;
          margin-top:.8rem;
          padding:0 1rem !important;
          height:1.7rem;
        }
      }
    }
  }
  .v-tab {
    &.v-tab--active {
      border-bottom: 2px solid $muted;
    }
  }

  .v-tabs-items {
    .v-sheet.v-card:not(.v-sheet--outlined) {
      box-shadow: unset;
    }
  }
}

</style>
